import React, { useMemo } from 'react';
import type { IEventDetailWithRegion } from '../../EventDetailSidebar/types';
import type { IHighPriorityEventItem } from '@op/components/ChartsEvents/types';
import CardCommon from '@op/components/CardCommon';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_TM } from '@op/utils/format';
import SvgIcon from '@home/components/SvgIcon';
import { getLocale } from 'umi';

interface IBubbleHoverContentProps {
  hoverEventDetails?: IEventDetailWithRegion[];
  hoverLoading?: boolean;
  /** 当前 hover 的事件，用于通过 tagName 匹配 hoverEventDetails 中正确的 region 数据 */
  event?: IHighPriorityEventItem;
  trendText?: any;
  handleEventClick?: (
    event: IHighPriorityEventItem,
    source?: 'detail' | 'event',
  ) => void;
}

export default function BubbleHoverContent({
  // hoverEventDetails = [],
  // hoverLoading = false,
  trendText,
  event,
  handleEventClick,
}: IBubbleHoverContentProps) {
  const { formatMessage, isEn } = useLanguage();

  const { detail, sortedFactors, title, ratio, tagName } = useMemo(() => {
    const { allFactors, ratio = 0, tagNameZh, tagNameEn } = event || {};
    // 按贡献度从高到低排序
    const sortedFactors = (allFactors || []).sort(
      (a: any, b: any) => (b.contribution ?? 0) - (a.contribution ?? 0),
    );
    const lang = getLocale();
    const trend = ratio > 0 ? 'up' : 'down';
    return {
      detail: event,
      sortedFactors,
      ratio,
      tagName: isEn ? tagNameEn : tagNameZh,
      title: trendText[trend][lang],
    };
  }, [event, formatMessage]);

  return (
    <CardCommon
      // loading={hoverLoading}
      empty={!detail}
      loadingHeight={80}
      emptyHeight={80}
      className="dbo-priority-wrap-cardCommon"
    >
      {detail && (
        <div className="dbo-priority-wrap">
          {/* 头部标题+涨幅+日期 */}
          <div className="dbo-priority-header">
            <h3 className="dbo-priority-title">
              {tagName} {title}
            </h3>
            <span
              className="dbo-priority-detail"
              onClick={(e) => {
                e.stopPropagation();
                handleEventClick?.(detail);
                return;
              }}
            >
              {formatMessage('Details')}
            </span>
            <div className="dbo-priority-meta-row">
              <div className="dbo-priority-meta-trend">
                <SvgIcon
                  type={ratio > 0 ? 'laugh-green-color' : 'laugh-red-color'}
                />
                <PercentageItem
                  percentage={detail.ratio || 0}
                  showIcon={true}
                />
              </div>

              <span className="dbo-priority-meta-text">
                {DBO_TM.timeTitle({
                  start_time: detail?.start_time,
                  end_time: detail?.end_time || detail.start_time,
                  date_type: 'daily',
                })}
              </span>
            </div>
          </div>
          {/* Attribution Breakdown：全部展示但最多2行，不展开 */}
          {sortedFactors.length > 0 && (
            <div className="dbo-priority-factors">
              <div className="dbo-priority-section-title dbo-priority-section-title--gradient">
                <span className="dbo-priority-section-title-icon" />
                <span className="dbo-priority-section-title-text">
                  {formatMessage('Attribution Breakdown')}(
                  {sortedFactors.length})
                </span>
                <span className="dbo-priority-section-title-tips">
                  {formatMessage("Contribution(↓)")}
                </span>
              </div>
              <div className="dbo-priority-factors-list">
                {sortedFactors.map((factor, index) => (
                  <div key={index} className="dbo-priority-factors-item">
                    <div className="dbo-priority-factors-header">
                      <span className="dbo-priority-factors-name">
                        {factor.name}
                      </span>
                      <PercentageItem
                        percentage={(factor.contribution || 0) * 100}
                        showIcon={false}
                      />
                    </div>
                    <div className="dbo-priority-factors-content">
                      <div className="dbo-priority-factors-bar">
                        <div
                          className="dbo-priority-factors-bar-inner"
                          style={{
                            width: (factor.contribution || 0) * 100 + '%',
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </CardCommon>
  );
}
